import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import CharacterCard from "@/components/feature/CharacterCard";
import { allCharacters, paginate, totalPages } from "@/utils/content";

const PAGE_SIZE = 8;
const categories = ["全部", "主角", "反派", "次要角色"];

export default function Characters() {
  const [category, setCategory] = useState("全部");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return allCharacters.filter((c) => {
      const matchCategory = category === "全部" || c.category === category;
      const matchKeyword =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.nameJp.toLowerCase().includes(q) ||
        c.epithet.toLowerCase().includes(q);
      return matchCategory && matchKeyword;
    });
  }, [category, keyword]);

  const pageCount = totalPages(filtered.length, PAGE_SIZE);
  const current = Math.min(page, pageCount);
  const visible = paginate(filtered, current, PAGE_SIZE);

  const resetFilters = () => {
    setCategory("全部");
    setKeyword("");
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        eyebrow="角色资料"
        icon="ri-user-star-line"
        title="火影忍者角色大全"
        description="按身份分类浏览角色资料，包含主角、反派与次要角色的背景介绍与能力解析。"
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setCategory(item);
                  setPage(1);
                }}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  category === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 md:w-72">
            <i className="ri-search-line text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              placeholder="输入角色名搜索"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => {
                  setKeyword("");
                  setPage(1);
                }}
                className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-400 hover:bg-background-200 hover:text-foreground-800 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-5 text-sm text-foreground-500">
          共找到 <strong className="text-foreground-900">{filtered.length}</strong> 位角色
          {category !== "全部" && ` · 身份「${category}」`}
        </p>

        {visible.length === 0 ? (
          <div className="mt-5">
            <EmptyState
              title="暂无匹配角色"
              description="换个角色名，或切换其它身份分类再试试。"
              onReset={resetFilters}
            />
          </div>
        ) : (
          <>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {visible.map((character) => (
                <CharacterCard key={character.id} character={character} />
              ))}
            </div>
            <Pagination page={current} total={pageCount} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}